import * as THREE from "three";
import { disposeMaterial } from "utils/functions.utils";
import { IObject, ObjectTypes } from "utils/interface.utils";
import { WorkspaceContext } from "interfaces/editor_workspace.interface";
import { cleanupTransformBox, renderTransformBox } from "components/editor_workspace/scene/transform_box.helper";

export const createObjectSocial = async (workspace: WorkspaceContext, objectId: string, properties: IObject) => {
    const { rendererRef, sceneRef, cameraRef, targetImageRef, objectsRef, selectedObjectIDRef, selectedTransformControlRef } = workspace.refs;
    if (!sceneRef.current || !cameraRef.current || !rendererRef.current) return;

    const scene = sceneRef.current;
    const camera = cameraRef.current;
    const targetImageGroup = targetImageRef.current;

    try {
        const objectSocial = await renderObjectSocial(workspace, scene, properties, objectId, targetImageGroup);

        // The texture loads asynchronously, so a newer build of this icon may have replaced this
        // one while it loaded. A replaced icon is already off the scene: registering or boxing it
        // would leave the live icon unregistered and a stray box behind.
        if (targetImageGroup && !objectSocial.mesh.parent) return;

        objectsRef.current.set(objectId, {
            mesh: objectSocial.mesh,
            cleanup: objectSocial.cleanup
        });

        // A newly added object is selected with the Position tool already active; the tool
        // effect ran before this mesh existed (textures load async), so attach its box here.
        if (objectId === selectedObjectIDRef.current && selectedTransformControlRef.current) {
            renderTransformBox(workspace, objectSocial.mesh);
        }

        if (rendererRef.current) {
            rendererRef.current.render(scene, camera);
        }
    } catch (error) {
        console.error("Error creating social object:", error);
    }
};

export const updateObjectSocial = (workspace: WorkspaceContext, objectId: string, properties: IObject) => {
    const { rendererRef, sceneRef, cameraRef, dragSystemRef, rotationSystemRef, scaleSystemRef, objectsRef, allObjectsPreviousScaleRef, selectedTransformControlRef } = workspace.refs;
    const { selectedObjectID } = workspace;
    if (dragSystemRef.current?.isObjectBeingDraggedAndPositioned(objectId)) {
        console.log(`Skipping social update for ${objectId} - currently being dragged`);
        return;
    }

    if (rotationSystemRef.current?.isObjectBeingDraggedAndRotated(objectId)) {
        console.log(`Skipping social update for ${objectId} - currently being rotated`);
        return;
    }

    if (scaleSystemRef.current?.isObjectBeingDraggedAndScaled(objectId)) {
        console.log(`Skipping social update for ${objectId} - currently being scaled`);
        return;
    }

    const objectData = objectsRef.current.get(objectId);
    if (!objectData || !objectData.mesh) return;

    const { color, bgColor, src, positionX, positionY, positionZ, rotationX, scaleX, scaleY, renderOrder } = properties;

    const mesh = objectData.mesh as THREE.Group;

    if (positionX !== undefined && mesh.position.x !== positionX) mesh.position.x = positionX;
    if (positionY !== undefined && mesh.position.y !== positionY) mesh.position.y = positionY;
    if (positionZ !== undefined && mesh.position.z !== positionZ) mesh.position.z = positionZ;

    if (mesh.userData.transformBox) {
        mesh.userData.transformBox.position.set(positionX || 0, positionY || 0, 0)
    }

    // mesh.rotation.z is radians and rotationX is degrees, so the incoming value has to be
    // converted before the change-detection compare -- same order as updateObjectImage and
    // updateObjectVideo. Comparing the two directly compared mismatched units.
    if (rotationX !== undefined) {
        const rotationXRadians = THREE.MathUtils.degToRad(rotationX);

        if (mesh.rotation.z !== rotationXRadians) {
            mesh.rotation.set(0, 0, rotationXRadians);

            if (mesh.userData.transformBox) {
                mesh.userData.transformBox.rotation.z = rotationXRadians;
            }
        }
    }

    const bgMesh = mesh.children.find(child => child.userData?.role === 'background') as THREE.Mesh | undefined;
    const iconMesh = mesh.children.find(child => child.userData?.role === 'icon') as THREE.Mesh | undefined;

    if (bgMesh && iconMesh) {
        // Background/icon geometry is a fixed unit size (see renderObjectSocial) --
        // scaleX/scaleY are applied as the group's Object3D scale, same as
        // updateObjectImage/updateObjectButton, so this is the single source of truth
        // instead of also rebaking scaleX/scaleY into the geometry (which double-applied
        // the scale on top of the transform-box drag system's own mesh.scale multiplier and
        // compounded it on every subsequent property update).
        const getPreviousObjectScale = allObjectsPreviousScaleRef.current.find(
            (object: { ID: string | null, scale: { x: number, y: number, z: number } }) => object.ID === objectId
        );

        let scaleChanged = false;

        if (getPreviousObjectScale?.scale) {
            if (scaleX !== undefined && scaleX !== getPreviousObjectScale.scale.x) {
                mesh.scale.x = Math.max(scaleX, 0.001);
                getPreviousObjectScale.scale.x = scaleX;
                scaleChanged = true;
            }

            if (scaleY !== undefined && scaleY !== getPreviousObjectScale.scale.y) {
                mesh.scale.y = Math.max(scaleY, 0.001);
                getPreviousObjectScale.scale.y = scaleY;
                scaleChanged = true;
            }
        }

        // The transform box (drawn from the mesh's bounding size at creation/selection time)
        // doesn't track scale changes automatically -- resync it so the handles match the
        // new size, same as updateObjectButton/updateObjectText do after their own
        // mesh/geometry changes.
        if (scaleChanged && objectId === selectedObjectID && selectedTransformControlRef.current) {
            renderTransformBox(workspace, mesh);
        }
    }

    if (renderOrder !== undefined) {
        mesh.renderOrder = renderOrder;
        mesh.userData.renderOrder = renderOrder;

        mesh.children.forEach(child => {
            child.renderOrder = renderOrder;
            if (child.userData) child.userData.renderOrder = renderOrder;
        });
    }

    if (bgMesh && bgColor) {
        const bgMaterial = bgMesh.material as THREE.MeshBasicMaterial;
        bgMaterial.color.set(bgColor);
    }

    // Update icon color if needed
    if (iconMesh && color && src) {
        updateIconColor(iconMesh, src, color).then(() => {
            // Re-render only after texture update completes
            if (sceneRef.current && cameraRef.current && rendererRef.current) {
                rendererRef.current.render(sceneRef.current, cameraRef.current);
            }
        }).catch(error => {
            console.error("Error updating icon color:", error);
        });
    } else {
        // Re-render immediately if no texture update is needed
        if (sceneRef.current && cameraRef.current && rendererRef.current) {
            rendererRef.current.render(sceneRef.current, cameraRef.current);
        }
    }
};

// Separate function to update icon color and texture
export const updateIconColor = async (iconMesh: THREE.Mesh, src: string, color: THREE.Color) => {
    try {
        const newTexture = await createColoredSVGTexture(src, color) as THREE.Texture;
        const iconMaterial = iconMesh.material as THREE.MeshBasicMaterial;

        if (iconMaterial.map) {
            iconMaterial.map.dispose();
        }

        iconMaterial.map = newTexture;
        iconMaterial.needsUpdate = true;
        return true;
    } catch (error) {
        console.error("Error updating icon color:", error);
        return false;
    }
};

export const renderObjectSocial = async (workspace: WorkspaceContext, scene: THREE.Scene, socialProps: IObject, objectId: string, targetImageGroup: THREE.Group | null) => {
    const { allObjectsPreviousScaleRef } = workspace.refs;
    const { src, color, bgColor, positionX, positionY, positionZ, rotationX, scaleX, scaleY, renderOrder } = socialProps;

    const parentObject = targetImageGroup || scene;

    const socialGroup = new THREE.Group();

    const existingChild = parentObject.children.find(child => child.userData.id === objectId);
    if (existingChild) {
        parentObject.remove(existingChild);
    }

    parentObject.add(socialGroup);

    // Background/icon geometry is a fixed unit size -- scaleX/scaleY are applied once below
    // as the group's Object3D scale, the same single-source-of-truth pattern
    // renderObjectImage uses (PlaneGeometry(1, 1) + imageGroup.scale.set(scaleX, scaleY, ...)).
    // Baking scaleX/scaleY into the geometry AND relying on the transform-box drag system's
    // mesh.scale multiplier double-applies the scale and compounds it on every subsequent
    // property update (see updateObjectSocial).
    const iconBgGeometry = new THREE.CircleGeometry(0.5, 32);
    const iconBgMaterial = new THREE.MeshBasicMaterial({
        color: bgColor,
        transparent: true
    });
    const iconBgMesh = new THREE.Mesh(iconBgGeometry, iconBgMaterial);
    iconBgMesh.userData.role = 'background';

    // Add the background to the group
    socialGroup.add(iconBgMesh);

    // Set the position of the social icon
    socialGroup.position.set(positionX, positionY, positionZ);
    socialGroup.scale.set(scaleX, scaleY, 1);

    // Apply the saved rotation at creation time, the same way renderObjectImage,
    // renderObjectButton and renderObjectText already do. This is the only path that runs when
    // an existing project is reopened via Edit (objectsRef is empty, so reconciliation takes
    // the create branch), so without this the saved rotationX was silently dropped and the
    // icon always drew upright -- even though Redux and the Properties panel held the correct
    // value, and dragging the rotate handle or moving the Rotation slider applied it fine via
    // updateObjectSocial.
    const rotationXRadians = THREE.MathUtils.degToRad(rotationX || 0);
    socialGroup.rotation.set(0, 0, rotationXRadians);

    // Add user data to make this object interactive
    socialGroup.userData.isDraggable = true;
    socialGroup.userData.id = objectId;
    socialGroup.userData.objectType = ObjectTypes.SOCIAL;

    allObjectsPreviousScaleRef.current.push({
        ID: objectId,
        scale: { x: scaleX, y: scaleY, z: 1 }
    });

    // Create and add the icon
    try {
        const texture = await createColoredSVGTexture(src, color);
        const iconWidth = 0.5;
        const iconHeight = 0.5;

        const iconGeometry = new THREE.PlaneGeometry(iconWidth, iconHeight);
        const iconMaterial = new THREE.MeshBasicMaterial({
            map: texture as THREE.Texture,
            transparent: true
        });

        const iconMesh = new THREE.Mesh(iconGeometry, iconMaterial);
        iconMesh.position.set(0, 0, 0);
        iconMesh.userData.role = 'icon';
        socialGroup.add(iconMesh);

        socialGroup.renderOrder = renderOrder;
        socialGroup.userData.renderOrder = renderOrder;

        socialGroup.children.forEach(child => {
            child.renderOrder = renderOrder;
            if (child.userData) child.userData.renderOrder = renderOrder;
        });

        return {
            mesh: socialGroup,
            cleanup: () => {
                // The transform box is a sibling of the object, so removing the object alone leaves it on the canvas.
                cleanupTransformBox(workspace, socialGroup);
                socialGroup.traverse((object) => {
                    if (object instanceof THREE.Mesh) {
                        if (object.geometry) object.geometry.dispose();

                        if (object.material) {
                            // Handle array of materials
                            if (Array.isArray(object.material)) {
                                object.material.forEach(material => disposeMaterial(material));
                            } else {
                                disposeMaterial(object.material);
                            }
                        }
                    }
                });

                const existingChild = parentObject.children.find(child => child.userData.id === objectId);
                if (existingChild) {
                    parentObject.remove(existingChild);
                }

                // Clear any user data references
                if (socialGroup.userData) {
                    Object.keys(socialGroup.userData).forEach(key => {
                        socialGroup.userData[key] = null;
                    });
                }
            }
        };
    } catch (error) {
        console.error("Error creating icon:", error);
        return {
            mesh: socialGroup,
            cleanup: () => {
                cleanupTransformBox(workspace, socialGroup);
                iconBgGeometry.dispose();
                iconBgMaterial.dispose();
                if (parentObject) parentObject.remove(socialGroup);
            }
        };
    }
};

export const createColoredSVGTexture = (svgUrl: string, colorValue: THREE.Color) => {
    return new Promise((resolve, reject) => {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        const iconWidth = 128;
        const iconHeight = 128;

        canvas.width = iconWidth;
        canvas.height = iconHeight;

        if (!ctx) {
            reject(new Error('Canvas 2D context not available'));
            return;
        }

        const img = new Image();
        img.crossOrigin = 'anonymous';

        img.onload = () => {
            ctx.clearRect(0, 0, canvas.width, canvas.height);
            ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

            const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
            const data = imageData.data;

            const colorObj = new THREE.Color(colorValue);
            const r = Math.floor(colorObj.r * 255);
            const g = Math.floor(colorObj.g * 255);
            const b = Math.floor(colorObj.b * 255);

            for (let i = 0; i < data.length; i += 4) {
                if (data[i + 3] > 0) {
                    data[i] = r;
                    data[i + 1] = g;
                    data[i + 2] = b;
                }
            }

            ctx.putImageData(imageData, 0, 0);

            const texture = new THREE.CanvasTexture(canvas);
            texture.needsUpdate = true;
            resolve(texture);
        };

        img.onerror = (error) => {
            reject(error);
        };

        img.src = svgUrl;
    });
};
